/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './DocumentDelivery.scss';

import { bool, object } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { flatten, isEmpty } from 'lodash';

import { Spinner } from '@trp-beacon-ui-kit/react-communication';
import Store from 'state/Store';
import PreferencesHeader from 'components/layouts/Preferences/PreferencesHeader';
import FixedSubmitFooter from 'components/layouts/Preferences/shared/FixedSubmitFooter/FixedSubmitFooter';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import PreferencesSection from 'components/layouts/Preferences/shared/PreferencesSection';
import ConsentModal from 'components/layouts/Preferences/DocumentDelivery/ConsentModal';
import If from 'components/shared/If/If';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import { openBrokerageAuthTab } from 'services/ExternalAuthorizationURLBuilder';
import {
  CONFIRMATIONS_SERVICE_TYPE_ID,
  documentDeliveryInitialState,
  documentDeliveryServiceTypesMapByName,
  generalGroupIdMap,
  PROSPECTUS_SERVICE_TYPE_ID,
  serviceFeeWaiverTypeIds,
  STATEMENTS_SERVICE_TYPE_ID,
  YE_PAPER_STATEMENTS_SERVICE_TYPE_ID
} from 'services/Preferences';
import {
  getCustomerDocumentDelivery,
  updateDocumentDelivery
} from 'services/Rest';
import { getDocumentDeliveryEnrollments } from 'state/subscriptions/SubscriptionsSelectors';
import {
  getCustomerPlanningGroupId,
  getPrimaryEmailAddress,
  isActiveHousehold
} from 'state/customer/CustomerSelectors';
import { getAllVisibleAccounts } from 'state/account/AccountSelectors';
import {
  hasActiveAdvisory,
  hasActivePlus,
  hasAlaska529,
  hasBrokerage,
  hasMaryland529,
  hasMutualFunds,
  hasNational529,
  isMobileLayout,
  getView,
  hasAllViewOnlyNational529,
  hasAllViewOnlyMaryland529,
  hasAllViewOnlyAlaska529,
  isEligibleForRetailProxy,
  isEligibleForRetailConfirmsAndProspectus,
  isEligibleForRetailStatements,
  isEligibleForMarylandPaperless,
  isEligibleForNationalPaperless,
  isEligibleForAlaskaPaperless,
  has529Only,
  hasAllViewOnlyMutualFund,
  hasAllViewOnlyBrokerage,
  hasAllViewOnlyActivePlus,
  isSalesforceRepEnabled
} from 'state/view/ViewSelectors';
import { InvalidOrMissingEmail } from 'components/layouts/Preferences/InvalidOrMissingEmail';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import { getHasInvalidOrNoEmailAddress } from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';

import DocumentDeliverySection from './DocumentDeliverySection/DocumentDeliverySection';
import DocumentDeliverySectionHeader from './DocumentDeliverySectionHeader/DocumentDeliverySectionHeader';
import DocumentDeliverySectionRow from './DocumentDeliverySectionRow/DocumentDeliverySectionRow';
import AllPaperlessBanner from './DocumentDeliverySection/PaperlessBanner/AllPaperlessBanner';
import GoPaperlessBanner from './DocumentDeliverySection/PaperlessBanner/GoPaperlessBanner';

import {
  removeUnchangedSelections,
  viewExclusions
} from './DocumentDeliveryFilters';
import {
  hideFixedFooterSaveOptions,
  showFixedFooterSaveOptions
} from 'state/preferences/PreferencesActions';

const BrokerageLink = () => (
  <button
    className="document-delivery-brokerage-link text-link"
    id="document-delivery-brokerage-link"
    onClick={() => openBrokerageAuthTab(undefined, 'EDELIVERY')}>
    paperless Prospectuses and Proxy/Shareholder Communications
  </button>
);

const BrokerageLinkWithPermissions = WithUserPermissions(BrokerageLink);

class DocumentDelivery extends React.Component {
  state = {
    loading: true,
    allPaperless: this.buildAllPaperless(),
    documentDelivery: {},
    showConsentModal: false,
    loadedDocumentDelivery: {}
  };

  componentDidMount() {
    getCustomerDocumentDelivery();
    Store.dispatch(hideFixedFooterSaveOptions());
  }

  componentDidUpdate(prevProps) {
    const { documentDelivery, requestStatus } = this.props;

    const { documentDelivery: updatedDocumentDelivery } = this.state;

    if (
      requestStatus.documentDelivery === 'RECEIVED' &&
      prevProps.requestStatus.documentDelivery !== 'RECEIVED'
    ) {
      this.setState({
        loading: false,
        loadedDocumentDelivery: documentDelivery,
        allPaperless: this.buildAllPaperless(),
        documentDelivery
      });
    }

    if (
      requestStatus.updateDocumentDelivery !==
      prevProps.requestStatus.updateDocumentDelivery
    ) {
      if (requestStatus.updateDocumentDelivery === 'RECEIVED') {
        this.setState({
          loadedDocumentDelivery: updatedDocumentDelivery, // needed to reset sticky save bar subsequent saves
          showConsentModal: false
        });
      }
      if (requestStatus.updateDocumentDelivery === 'ERROR') {
        // reset to received if update doesnt go through
        this.setState({
          showConsentModal: false,
          loadedDocumentDelivery: documentDelivery,
          documentDelivery
        });
      }
    }
  }

  buildAllPaperless() {
    const { documentDelivery } = this.props;

    return Object.keys(documentDelivery)
      .map(Number)
      .every((serviceTypeId) => documentDelivery[serviceTypeId]);
  }

  buildDocumentDelivery() {
    const { documentDelivery } = this.props;
    return {
      ...documentDelivery
    };
  }

  handleShowConsentModal = () => this.setState({ showConsentModal: true });

  handleHideConsentModal = () => this.setState({ showConsentModal: false });

  getEligibilityRules = () => {
    const {
      hasActiveAdvisory,
      hasActivePlus,
      hasAlaska529,
      hasBrokerage,
      hasMaryland529,
      hasMutualFunds,
      hasNational529,
      hasAllViewOnlyMaryland529,
      hasAllViewOnlyAlaska529,
      hasAllViewOnlyNational529,
      hasAllViewOnlyMutualFund,
      hasAllViewOnlyBrokerage,
      hasAllViewOnlyActivePlus,
      isEligibleForRetailProxy,
      isEligibleForRetailConfirmsAndProspectus,
      isEligibleForRetailStatements,
      isEligibleForMarylandPaperless,
      isEligibleForNationalPaperless,
      isEligibleForAlaskaPaperless,
      has529Only
    } = this.props;

    return {
      hasActiveAdvisory,
      hasActivePlus,
      hasAlaska529,
      hasBrokerage,
      hasMaryland529,
      hasMutualFunds,
      hasNational529,
      hasAllViewOnlyMaryland529,
      hasAllViewOnlyAlaska529,
      hasAllViewOnlyNational529,
      hasAllViewOnlyMutualFund,
      hasAllViewOnlyBrokerage,
      hasAllViewOnlyActivePlus,
      isEligibleForRetailProxy,
      isEligibleForRetailConfirmsAndProspectus,
      isEligibleForRetailStatements,
      isEligibleForMarylandPaperless,
      isEligibleForNationalPaperless,
      isEligibleForAlaskaPaperless,
      has529Only
    };
  };

  handleCancel = () => {
    const { history } = this.props;
    history.push('/profile');
  };

  handleSave = () => {
    const { documentDelivery, loadedDocumentDelivery } = this.state;

    const { isSalesforceRepEnabled, emailAddress } = this.props;

    const preferences = flatten(
      Object.entries(documentDeliveryInitialState(isSalesforceRepEnabled)).map(
        ([section, deliveryPreferences]) =>
          deliveryPreferences.map((pref) => {
            return { ...pref, section };
          })
      )
    );

    const eligibilityRules = this.getEligibilityRules();

    const complianceSubscriptions = preferences
      .filter((preference) => viewExclusions(preference, eligibilityRules))
      .filter((preference) =>
        removeUnchangedSelections(
          preference,
          loadedDocumentDelivery,
          documentDelivery
        )
      )
      .map(({ serviceTypeId }) => {
        return {
          subscriptionId: Number(serviceTypeId),
          subscribed: Boolean(documentDelivery[serviceTypeId])
        };
      });

    if (isSalesforceRepEnabled) {
      if (
        documentDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID] !==
        loadedDocumentDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID]
      ) {
        complianceSubscriptions.push({
          subscriptionId: Number(YE_PAPER_STATEMENTS_SERVICE_TYPE_ID),
          subscribed: Boolean(
            documentDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID]
          )
        });
      }
    }

    const payload = {
      subscriptions: {
        complianceSubscriptions
      }
    };

    updateDocumentDelivery(payload, emailAddress);
  };

  selectAllPaperless = () => {
    const { isSalesforceRepEnabled } = this.props;
    const newDocumentDelivery = {};
    const eligibilityRules = this.getEligibilityRules();

    const preferences = flatten(
      Object.entries(documentDeliveryInitialState(isSalesforceRepEnabled)).map(
        ([section, deliveryPreferences]) =>
          deliveryPreferences.map((pref) => {
            return { ...pref, section };
          })
      )
    );

    preferences
      .filter((pref) => viewExclusions(pref, eligibilityRules))
      .forEach((pref) => {
        newDocumentDelivery[pref.serviceTypeId] = true;
      });

    this.setState({
      allPaperless: true,
      documentDelivery: newDocumentDelivery
    });

    this.showSaveOptions();
  };

  updateDeliverySelection = (serviceTypeId, isEmailDelivery) => {
    const { documentDelivery, loadedDocumentDelivery } = this.state;

    const newDocumentDelivery = {
      ...documentDelivery
    };

    if (Array.isArray(serviceTypeId)) {
      serviceTypeId.forEach(
        (id) => (newDocumentDelivery[id] = isEmailDelivery)
      );
    } else {
      newDocumentDelivery[serviceTypeId] = isEmailDelivery;
    }

    if (
      serviceTypeId === STATEMENTS_SERVICE_TYPE_ID &&
      isSalesforceRepEnabled
    ) {
      if (newDocumentDelivery[STATEMENTS_SERVICE_TYPE_ID] === false) {
        newDocumentDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID] = false;
      } else if (newDocumentDelivery[STATEMENTS_SERVICE_TYPE_ID] === true) {
        newDocumentDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID] =
          loadedDocumentDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID];
      }
    }

    if (serviceTypeId === CONFIRMATIONS_SERVICE_TYPE_ID && isEmailDelivery) {
      newDocumentDelivery[PROSPECTUS_SERVICE_TYPE_ID] = true;
    }

    if (serviceTypeId === PROSPECTUS_SERVICE_TYPE_ID && !isEmailDelivery) {
      newDocumentDelivery[CONFIRMATIONS_SERVICE_TYPE_ID] = false;
    }

    const allPaperless = Object.keys(newDocumentDelivery)
      .map(Number)
      .filter((n) => n !== documentDeliveryServiceTypesMapByName.proxy)
      .every((serviceTypeId) => newDocumentDelivery[serviceTypeId]);

    this.setState({
      allPaperless,
      documentDelivery: newDocumentDelivery
    });

    this.showSaveOptions();
  };

  serviceFeeCriteriaWaverMet = () => {
    const { documentDelivery } = this.state;
    return serviceFeeWaiverTypeIds.every(
      (serviceTypeId) => documentDelivery[serviceTypeId]
    );
  };

  changedToEmailDelivery = () => {
    const { documentDelivery } = this.state;
    return Object.keys(documentDelivery).some((typeId) => {
      return documentDelivery[typeId] && !this.props.documentDelivery[typeId];
    });
  };

  checkIfDisableSubmit = () => {
    const { documentDelivery } = this.state;
    return !Object.keys(documentDelivery).some(
      (id) => documentDelivery[id] !== this.props.documentDelivery[id]
    );
  };

  setEmailForType = (type) => this.updateDeliverySelection(type, true);

  setPaperMailForType = (type) => this.updateDeliverySelection(type, false);

  selectAllGeneralPaperless = () => {
    const generalSelections = Object.values(generalGroupIdMap).reduce(
      (acc, serviceTypeId) => ({ ...acc, [serviceTypeId]: true }),
      {}
    );
    const newDocumentDelivery = {
      ...this.state.documentDelivery,
      ...generalSelections
    };

    this.setState({ documentDelivery: newDocumentDelivery });
    this.showSaveOptions();
  };

  isGeneralGroupAllEmail = () =>
    // While `Proxy Materials` is in the general section it is not part of the concideration for the banner
    Object.values(generalGroupIdMap).every(
      (serviceTypeId) => this.state.documentDelivery[serviceTypeId]
    );

  /*
   * Filter Predicate to ensure that only the statement and confirmation row shows when there are no mutual funds
   * For story US170028- Brokerage Notes
   * */
  filterBrokerageOptions = ({ serviceTypeId }) => {
    return (
      serviceTypeId === documentDeliveryServiceTypesMapByName.statements ||
      serviceTypeId === documentDeliveryServiceTypesMapByName.confirmations
    );
  };

  /*
   * Predicate to indicate that prospectus and proxy should have a `*` if there is a brokerage account
   * For story US170028 - Brokerage Notes
   * */
  addExceptionNote = (serviceTypeId) => {
    const { hasBrokerage, hasMutualFunds } = this.props;
    return (
      hasBrokerage &&
      hasMutualFunds &&
      (serviceTypeId === documentDeliveryServiceTypesMapByName.prospectus ||
        serviceTypeId === documentDeliveryServiceTypesMapByName.proxy)
    );
  };

  showSaveOptions = () => {
    const { loading } = this.state;

    if (!loading) {
      Store.dispatch(showFixedFooterSaveOptions());
    }
  };

  updateYEPaperStatements = (checked) => {
    const { documentDelivery } = this.state;

    const newDocumentDelivery = {
      ...documentDelivery
    };

    newDocumentDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID] = checked;

    this.setState({ documentDelivery: newDocumentDelivery });
    this.showSaveOptions();
  };

  render() {
    const {
      loading,
      showConsentModal,
      loadedDocumentDelivery,
      documentDelivery
    } = this.state;

    const {
      accounts,
      customerPlanningGroupId,
      isActiveHousehold,
      isSalesforceRepEnabled,
      hasActiveAdvisory,
      hasActivePlus,
      hasAlaska529,
      hasBrokerage,
      hasMaryland529,
      hasMutualFunds,
      hasNational529,
      hasInvalidOrMissingEmail,
      view,
      hasAllViewOnlyMaryland529,
      hasAllViewOnlyAlaska529,
      hasAllViewOnlyNational529,
      hasAllViewOnlyMutualFund,
      hasAllViewOnlyBrokerage,
      hasAllViewOnlyActivePlus,
      hideFixedFooterSaveOptions
    } = this.props;
    const eligibilityRules = this.getEligibilityRules();
    const hasActiveHousehold =
      isActiveHousehold &&
      !isEmpty(accounts) &&
      accounts.some(
        (account) =>
          account.financialPlanningGroupId === customerPlanningGroupId
      );
    const {
      activeAdvisory,
      activePlus,
      akCollegeSavings,
      generalInvesting,
      mdCollegeSavings,
      trpCollegeSavings
    } = documentDeliveryInitialState(isSalesforceRepEnabled);

    // const serviceFeeCriteriaWaverMet = this.serviceFeeCriteriaWaverMet();

    const changedToEmailDelivery = this.changedToEmailDelivery();

    const disableSubmit = this.checkIfDisableSubmit();

    // get all eligible row titles
    const getEligibleServiceTypeIds = generalInvesting
      .filter(({ isEligible }) => isEligible(eligibilityRules))
      .map(({ serviceTypeId }) => serviceTypeId);

    const getBrokerageEligibleServiceTypeIds = generalInvesting
      .filter(({ isEligible }) => isEligible(eligibilityRules))
      .filter(this.filterBrokerageOptions)
      .map(({ serviceTypeId }) => serviceTypeId);

    const hasAllInvestingRows = serviceFeeWaiverTypeIds.every((id) =>
      getEligibleServiceTypeIds.includes(id)
    );
    const hasAllInvestingRowsBrk = serviceFeeWaiverTypeIds.every((id) =>
      getBrokerageEligibleServiceTypeIds.includes(id)
    );

    const GeneralInvestingMutualFundRows = generalInvesting
      .filter(({ isEligible }) => isEligible(eligibilityRules))
      .map(({ modalContent, note, serviceTypeId, showBanner, title }) => {
        return (
          <div className="document-delivery-section-row" key={title}>
            <DocumentDeliverySectionRow
              isEmailDelivery={documentDelivery[serviceTypeId]}
              disableEMailSelection={
                isSalesforceRepEnabled && !loadedDocumentDelivery[serviceTypeId]
              }
              modalContent={modalContent}
              serviceTypeId={serviceTypeId}
              title={this.addExceptionNote(serviceTypeId) ? title + '*' : title}
              updateDeliverySelection={this.updateDeliverySelection}
              hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
            />
            {note && <div className="document-delivery-row-note">{note}</div>}
            {showBanner &&
              !isSalesforceRepEnabled &&
              (this.isGeneralGroupAllEmail() ? (
                <AllPaperlessBanner
                  hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
                />
              ) : (
                hasAllInvestingRows && (
                  <GoPaperlessBanner
                    selectAllPaperless={this.selectAllGeneralPaperless}
                    hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
                  />
                )
              ))}
          </div>
        );
      });

    const GeneralInvestingBrokerageRows = generalInvesting
      .filter(({ isEligible }) => isEligible(eligibilityRules))
      .filter(this.filterBrokerageOptions)
      .map(({ modalContent, note, serviceTypeId, showBanner, title }) => {
        return (
          <div className="document-delivery-section-row" key={title}>
            <DocumentDeliverySectionRow
              isEmailDelivery={documentDelivery[serviceTypeId]}
              disableEMailSelection={
                isSalesforceRepEnabled && !loadedDocumentDelivery[serviceTypeId]
              }
              modalContent={modalContent}
              serviceTypeId={serviceTypeId}
              title={title}
              updateDeliverySelection={this.updateDeliverySelection}
              hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
            />
            {note && <div className="document-delivery-row-note">{note}</div>}
            {showBanner &&
              !isSalesforceRepEnabled &&
              (this.isGeneralGroupAllEmail() ? (
                <AllPaperlessBanner
                  hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
                />
              ) : (
                hasAllInvestingRowsBrk && (
                  <GoPaperlessBanner
                    selectAllPaperless={this.selectAllGeneralPaperless}
                    hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
                  />
                )
              ))}
          </div>
        );
      });

    const CollegeSavingsNationalRows = trpCollegeSavings
      .filter(({ isEligible }) => isEligible(eligibilityRules))
      .map(({ modalContent, serviceTypeId, title }) => {
        return (
          <div className="document-delivery-section-row" key={title}>
            <DocumentDeliverySectionRow
              isEmailDelivery={documentDelivery[serviceTypeId]}
              disableEMailSelection={
                isSalesforceRepEnabled && !loadedDocumentDelivery[serviceTypeId]
              }
              modalContent={modalContent}
              serviceTypeId={serviceTypeId}
              title={title}
              updateDeliverySelection={this.updateDeliverySelection}
              hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
            />
          </div>
        );
      });

    const CollegeSavingsMarylandRows = mdCollegeSavings
      .filter(({ isEligible }) => isEligible(eligibilityRules))
      .map(({ modalContent, serviceTypeId, title }) => {
        return (
          <div className="document-delivery-section-row" key={title}>
            <DocumentDeliverySectionRow
              isEmailDelivery={documentDelivery[serviceTypeId]}
              disableEMailSelection={
                isSalesforceRepEnabled && !loadedDocumentDelivery[serviceTypeId]
              }
              modalContent={modalContent}
              serviceTypeId={serviceTypeId}
              title={title}
              updateDeliverySelection={this.updateDeliverySelection}
              hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
            />
          </div>
        );
      });

    const CollegeSavingsAlaskaRows = akCollegeSavings
      .filter(({ isEligible }) => isEligible(eligibilityRules))
      .map(({ modalContent, serviceTypeId, title }) => {
        return (
          <div className="document-delivery-section-row" key={title}>
            <DocumentDeliverySectionRow
              isEmailDelivery={documentDelivery[serviceTypeId]}
              disableEMailSelection={
                isSalesforceRepEnabled && !loadedDocumentDelivery[serviceTypeId]
              }
              modalContent={modalContent}
              serviceTypeId={serviceTypeId}
              title={title}
              updateDeliverySelection={this.updateDeliverySelection}
              hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
            />
          </div>
        );
      });

    const APPRows = activePlus
      .filter(({ isEligible }) => isEligible(eligibilityRules))
      .map(
        ({ disableMailSelection, modalContent, serviceTypeId, title }, i) => {
          return (
            <div key={i} className="document-delivery-section-row">
              <DocumentDeliverySectionRow
                disableMailSelection={disableMailSelection}
                disableEMailSelection={
                  isSalesforceRepEnabled &&
                  !loadedDocumentDelivery[serviceTypeId]
                }
                isEmailDelivery={documentDelivery[serviceTypeId]}
                modalContent={modalContent}
                serviceTypeId={serviceTypeId}
                title={title}
                updateDeliverySelection={this.updateDeliverySelection}
                hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
              />
            </div>
          );
        }
      );

    const AdvisoryRows = activeAdvisory
      .filter(({ isEligible }) => isEligible(eligibilityRules))
      .map(
        ({ disableMailSelection, modalContent, serviceTypeId, title }, i) => {
          return (
            <div key={i} className="document-delivery-section-row">
              <DocumentDeliverySectionRow
                disableMailSelection={disableMailSelection}
                isEmailDelivery={documentDelivery[serviceTypeId]}
                modalContent={modalContent}
                serviceTypeId={serviceTypeId}
                title={title}
                updateDeliverySelection={this.updateDeliverySelection}
                hasInvalidOrMissingEmail={hasInvalidOrMissingEmail}
              />
            </div>
          );
        }
      );

    return loading ? (
      <div className="center">
        <Spinner />
      </div>
    ) : (
      <Fragment>
        {showConsentModal && (
          <ConsentModal
            onCancel={this.handleHideConsentModal}
            onSave={this.handleSave}
          />
        )}
        {!isSalesforceRepEnabled && (
          <PreferencesHeader>
            <div className="document-delivery-header">Document Delivery</div>
          </PreferencesHeader>
        )}
        <PreferencesSection className="document-delivery-preferences-section">
          <If test={hasInvalidOrMissingEmail}>
            <InvalidOrMissingEmail page="document delivery" view={view} />
          </If>

          <div className="all-paperless-container">
            {!isSalesforceRepEnabled && (
              <button
                alt="set all to paperless delivery"
                className="document-delivery-all-paperless"
                id="document-delivery-all-paperless"
                disabled={hasInvalidOrMissingEmail}
                onClick={this.selectAllPaperless}>
                Go paperless for all communications
              </button>
            )}
          </div>

          {(hasMutualFunds || hasBrokerage) &&
            !(hasAllViewOnlyBrokerage && hasAllViewOnlyMutualFund) && (
              <PreferencesRow className="document-delivery-preferences-row">
                <div className="document-delivery-main-content">
                  <DocumentDeliverySectionHeader
                    sectionHeader="General Investing Accounts"
                    selectAllPaperless={this.selectAllPaperless}
                    showToggleAll
                  />
                  {hasMutualFunds && !hasAllViewOnlyMutualFund && (
                    <DocumentDeliverySection>
                      {GeneralInvestingMutualFundRows.length ? (
                        GeneralInvestingMutualFundRows
                      ) : (
                        <div className="document-delivery-section-row">
                          No Delivery Options Available
                        </div>
                      )}
                    </DocumentDeliverySection>
                  )}
                  {!hasMutualFunds &&
                    hasBrokerage &&
                    !hasAllViewOnlyBrokerage && (
                      <DocumentDeliverySection>
                        {GeneralInvestingBrokerageRows.length ? (
                          GeneralInvestingBrokerageRows
                        ) : (
                          <div className="document-delivery-section-row">
                            No Delivery Options Available
                          </div>
                        )}
                      </DocumentDeliverySection>
                    )}

                  {isSalesforceRepEnabled && (
                    <div className="document-delivery-section-content">
                      <div className="document-delivery-section-row">
                        <Checkbox
                          checked={
                            documentDelivery[
                              YE_PAPER_STATEMENTS_SERVICE_TYPE_ID
                            ]
                          }
                          className="yepaperstatements-checkbox"
                          label={'Year-end Paper Statements'}
                          name={'yepaperstatements-checkbox'}
                          disabled={
                            documentDelivery[STATEMENTS_SERVICE_TYPE_ID] ===
                            false
                          }
                          onChange={this.updateYEPaperStatements}
                        />
                        <br />
                        <span>
                          This option is only available to clients who have
                          elected to receive statements via email. Selecting
                          this option does not forfeit the Account Fee Waiver.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </PreferencesRow>
            )}
          {hasNational529 && !hasAllViewOnlyNational529 && (
            <PreferencesRow className="document-delivery-preferences-row">
              <div className="document-delivery-main-content">
                <DocumentDeliverySectionHeader sectionHeader="T. Rowe Price College Savings Plan Accounts" />
                <DocumentDeliverySection>
                  {CollegeSavingsNationalRows.length > 0 ? (
                    CollegeSavingsNationalRows
                  ) : (
                    <div className="document-delivery-section-row">
                      No Delivery Options Available
                    </div>
                  )}
                </DocumentDeliverySection>
              </div>
            </PreferencesRow>
          )}
          {hasMaryland529 && !hasAllViewOnlyMaryland529 && (
            <PreferencesRow className="document-delivery-preferences-row">
              <div className="document-delivery-main-content">
                <DocumentDeliverySectionHeader sectionHeader="Maryland Senator Edward J. Kasemeyer College Investment Plan Accounts" />
                <DocumentDeliverySection>
                  {CollegeSavingsMarylandRows.length > 0 ? (
                    CollegeSavingsMarylandRows
                  ) : (
                    <div className="document-delivery-section-row">
                      No Delivery Options Available
                    </div>
                  )}
                </DocumentDeliverySection>
              </div>
            </PreferencesRow>
          )}
          {hasAlaska529 && !hasAllViewOnlyAlaska529 && (
            <PreferencesRow className="document-delivery-preferences-row">
              <div className="document-delivery-main-content">
                <DocumentDeliverySectionHeader sectionHeader="Alaska 529 Accounts" />
                <DocumentDeliverySection>
                  {CollegeSavingsAlaskaRows.length > 0 ? (
                    CollegeSavingsAlaskaRows
                  ) : (
                    <div className="document-delivery-section-row">
                      No Delivery Options Available
                    </div>
                  )}
                </DocumentDeliverySection>
              </div>
            </PreferencesRow>
          )}
          {hasActivePlus && !hasAllViewOnlyActivePlus && (
            <PreferencesRow className="document-delivery-preferences-row">
              <div className="document-delivery-main-content">
                <DocumentDeliverySectionHeader sectionHeader="ActivePlus Portfolios®" />
                <DocumentDeliverySection>
                  {APPRows.length > 0 ? (
                    APPRows
                  ) : (
                    <div className="document-delivery-section-row">
                      No Delivery Options Available
                    </div>
                  )}
                </DocumentDeliverySection>
              </div>
            </PreferencesRow>
          )}
          {hasActiveAdvisory && hasActiveHousehold && (
            <PreferencesRow className="document-delivery-preferences-row">
              <div className="document-delivery-main-content">
                <DocumentDeliverySectionHeader sectionHeader="Retirement Advisory Service™" />
                <DocumentDeliverySection>
                  {AdvisoryRows.length > 0 ? (
                    AdvisoryRows
                  ) : (
                    <div className="document-delivery-section-row">
                      No Delivery Options Available
                    </div>
                  )}
                </DocumentDeliverySection>
              </div>
            </PreferencesRow>
          )}
          <PreferencesRow className="document-delivery-preferences-row">
            <div className="document-delivery-footer">
              <div className="document-delivery-button-container">
                <FixedSubmitFooter
                  cancel={this.handleCancel}
                  requestStatusKey="updateDocumentDelivery"
                  shouldShow={!hideFixedFooterSaveOptions}
                  shouldDisableSubmit={disableSubmit}
                  submit={
                    changedToEmailDelivery
                      ? this.handleShowConsentModal
                      : this.handleSave
                  }
                />
              </div>
              {hasBrokerage && !hasAllViewOnlyBrokerage && (
                <p className="document-delivery-brokerage-note">
                  {hasMutualFunds && !hasAllViewOnlyMutualFund
                    ? '* Excludes Brokerage accounts. Please'
                    : 'For your Brokerage accounts, please'}{' '}
                  subscribe to the{' '}
                  {isSalesforceRepEnabled ? (
                    <span>
                      paperless Prospectuses and Proxy/Shareholder
                      Communications
                    </span>
                  ) : (
                    <BrokerageLinkWithPermissions
                      styleOverride={{ display: 'inline' }}
                      shouldDisable
                    />
                  )}{' '}
                  to enroll in email delivery for these documents.
                </p>
              )}
              <p className="document-delivery-footnote">
                <b>Please Note:</b> When you select email delivery, we&apos;ll
                send you an email alerting you when your documents are available
                online to view, print, and/or download. You can securely access
                the documents by logging onto your account online. To help
                protect against fraud, we may send confirmations of certain
                account maintenance requests to you by U.S. Mail (except T. Rowe
                Price ActivePlus Portfolios®).
              </p>
            </div>
          </PreferencesRow>
        </PreferencesSection>
      </Fragment>
    );
  }
}

DocumentDelivery.propTypes = {
  documentDelivery: object.isRequired,
  hasActiveAdvisory: object.isRequired,
  hasActivePlus: bool.isRequired,
  hasAlaska529: bool.isRequired,
  hasBrokerage: bool.isRequired,
  hasMaryland529: bool.isRequired,
  hasMutualFunds: bool.isRequired,
  hasNational529: bool.isRequired,
  history: object.isRequired,
  requestStatus: object.isRequired,
  has529Only: bool,
  isActiveHousehold: bool
};

const mapStateToProps = (state) => ({
  accounts: getAllVisibleAccounts(state),
  isActiveHousehold: isActiveHousehold(state),
  isSalesforceRepEnabled: isSalesforceRepEnabled(state),
  documentDelivery: getDocumentDeliveryEnrollments(state),
  hasActiveAdvisory: hasActiveAdvisory(state),
  hasActivePlus: hasActivePlus(state),
  hasAlaska529: hasAlaska529(state),
  hasBrokerage: hasBrokerage(state),
  hasMaryland529: hasMaryland529(state),
  hasMutualFunds: hasMutualFunds(state),
  hasNational529: hasNational529(state),
  hasAllViewOnlyMaryland529: hasAllViewOnlyMaryland529(state),
  hasAllViewOnlyAlaska529: hasAllViewOnlyAlaska529(state),
  hasAllViewOnlyNational529: hasAllViewOnlyNational529(state),
  hasAllViewOnlyMutualFund: hasAllViewOnlyMutualFund(state),
  hasAllViewOnlyBrokerage: hasAllViewOnlyBrokerage(state),
  hasAllViewOnlyActivePlus: hasAllViewOnlyActivePlus(state),
  hideFixedFooterSaveOptions: state.hideFixedFooterSaveOptions,
  customerPlanningGroupId: getCustomerPlanningGroupId(state),
  isMobileLayout: isMobileLayout(state),
  isEligibleForRetailProxy: isEligibleForRetailProxy(state),
  isEligibleForRetailConfirmsAndProspectus:
    isEligibleForRetailConfirmsAndProspectus(state),
  isEligibleForRetailStatements: isEligibleForRetailStatements(state),
  isEligibleForMarylandPaperless: isEligibleForMarylandPaperless(state),
  isEligibleForNationalPaperless: isEligibleForNationalPaperless(state),
  isEligibleForAlaskaPaperless: isEligibleForAlaskaPaperless(state),
  requestStatus: state.requestStatus,
  router: state.router,
  has529Only: has529Only(state),
  hasInvalidOrMissingEmail: getHasInvalidOrNoEmailAddress(state),
  view: getView(state),
  emailAddress: getPrimaryEmailAddress(state)
});

export default withRouter(connect(mapStateToProps)(DocumentDelivery));
